import React, { useState, useEffect } from "react";
import ModalWrapper from "../../components/ui/modals/ModalWrapper";
import Button from "../../components/ui/buttons/Button";
import { changePassword, dismissPasswordPrompt } from "../../services/api/core";

/**
 * Onboarding modal that prompts the user to change their password when the
 * account still uses a default or flagged credential. Shown from Home when
 * `personal_password` is false on the current user.
 *
 * @param {boolean} isOpen Modal visibility.
 * @param {Function} onClose Close without saving; modal may reappear on next visit.
 * @param {Function} onDismiss Mark prompt dismissed without changing password.
 * @param {Function} onComplete Called after a successful password change.
 * @param {string} username Username for password-manager accessibility.
 * @returns {JSX.Element|null}
 */
const PersonalPasswordModal = ({ isOpen, onClose, onDismiss, onComplete, username = '' }) => {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Client-side validation
  const validateForm = () => {
    const newErrors = {};

    if (!oldPassword) {
      newErrors.oldPassword = "Current password is required";
    }

    if (!newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (newPassword.length < 8) {
      newErrors.newPassword = "Password must be at least 8 characters";
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your new password";
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdatePassword = async () => {
    // Validate before submit
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await changePassword(oldPassword, newPassword, confirmPassword);

      if (response.success) {
        // Password changed successfully
        onComplete();
      } else {
        // Handle server-side validation errors
        if (response.errors) {
          const serverErrors = {};

          // Handle Django serializer errors
          if (response.errors.old_password) {
            serverErrors.oldPassword = Array.isArray(response.errors.old_password)
              ? response.errors.old_password[0]
              : response.errors.old_password;
          }

          if (response.errors.new_password) {
            serverErrors.newPassword = Array.isArray(response.errors.new_password)
              ? response.errors.new_password.join(". ")
              : response.errors.new_password;
          }

          if (response.errors.confirm_password) {
            serverErrors.confirmPassword = Array.isArray(response.errors.confirm_password)
              ? response.errors.confirm_password[0]
              : response.errors.confirm_password;
          }

          setErrors(serverErrors);
        } else if (response.message) {
          setErrors({ general: response.message });
        }
      }
    } catch (error) {
      console.error("Error updating password:", error);

      // Check if error response contains validation errors
      if (error.response && error.response.data) {
        const responseData = error.response.data;

        if (responseData.errors) {
          const serverErrors = {};

          // Handle Django serializer errors
          if (responseData.errors.old_password) {
            serverErrors.oldPassword = Array.isArray(responseData.errors.old_password)
              ? responseData.errors.old_password[0]
              : responseData.errors.old_password;
          }

          if (responseData.errors.new_password) {
            serverErrors.newPassword = Array.isArray(responseData.errors.new_password)
              ? responseData.errors.new_password.join(". ")
              : responseData.errors.new_password;
          }

          if (responseData.errors.confirm_password) {
            serverErrors.confirmPassword = Array.isArray(responseData.errors.confirm_password)
              ? responseData.errors.confirm_password[0]
              : responseData.errors.confirm_password;
          }

          setErrors(serverErrors);
        } else if (responseData.message) {
          setErrors({ general: responseData.message });
        } else {
          setErrors({ general: "An error occurred. Please try again." });
        }
      } else {
        setErrors({ general: "An error occurred. Please try again." });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDismiss = async () => {
    setIsSubmitting(true);

    try {
      await dismissPasswordPrompt();
      onDismiss();
    } catch (error) {
      console.error("Error dismissing password prompt:", error);
      setErrors({ general: "An error occurred. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setErrors({});
    }
  }, [isOpen]);

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose}>
      <div className="flex flex-col text-center">
        <h2 className="text-2xl font-bold mb-4">Update Your Password</h2>
        <p className="text-sm text-gray-600 mb-6">
          For security, we recommend updating your password to a personal one.
        </p>

        <form onSubmit={(e) => { e.preventDefault(); handleUpdatePassword(); }}>
        {/* Hidden username field for password manager accessibility */}
        <input
          type="text"
          id="username"
          name="username"
          autoComplete="username"
          value={username}
          readOnly
          style={{ position: 'absolute', left: '-9999px' }}
          tabIndex={-1}
          aria-hidden="true"
        />

        {/* Current Password Input */}
        <div className="mb-4">
          <label htmlFor="current-password" className="block text-left text-sm font-medium text-gray-700 mb-1">
            Current Password
          </label>
          <input
            type="password"
            id="current-password"
            name="current-password"
            autoComplete="current-password"
            className={`w-full p-2 border rounded-md ${
              errors.oldPassword ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="Enter current password"
            value={oldPassword}
            onChange={(e) => {
              setOldPassword(e.target.value);
              setErrors({ ...errors, oldPassword: "", general: "" });
            }}
            disabled={isSubmitting}
          />
          {errors.oldPassword && (
            <p className="mt-1 text-sm text-red-600 text-left">{errors.oldPassword}</p>
          )}
        </div>

        {/* New Password Input */}
        <div className="mb-4">
          <label htmlFor="new-password" className="block text-left text-sm font-medium text-gray-700 mb-1">
            New Password
          </label>
          <input
            type="password"
            id="new-password"
            name="new-password"
            autoComplete="new-password"
            className={`w-full p-2 border rounded-md ${
              errors.newPassword ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="Enter new password"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setErrors({ ...errors, newPassword: "", general: "" });
            }}
            disabled={isSubmitting}
          />
          {errors.newPassword && (
            <p className="mt-1 text-sm text-red-600 text-left">{errors.newPassword}</p>
          )}
          <p className="mt-1 text-xs text-gray-600 text-left">
            <i>Must be at least 8 characters</i>
          </p>
        </div>

        {/* Confirm Password Input */}
        <div className="mb-6">
          <label htmlFor="confirm-password" className="block text-left text-sm font-medium text-gray-700 mb-1">
            Confirm New Password
          </label>
          <input
            type="password"
            id="confirm-password"
            name="confirm-password"
            autoComplete="new-password"
            className={`w-full p-2 border rounded-md ${
              errors.confirmPassword ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setErrors({ ...errors, confirmPassword: "", general: "" });
            }}
            disabled={isSubmitting}
          />
          {errors.confirmPassword && (
            <p className="mt-1 text-sm text-red-600 text-left">{errors.confirmPassword}</p>
          )}
        </div>

        {/* General error message */}
        {errors.general && (
          <p className="mb-4 text-sm text-red-600">{errors.general}</p>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6">
          <Button
            onClick={handleDismiss}
            text={isSubmitting ? "Processing..." : "No, thanks"}
            variant={isSubmitting ? "disabled" : "secondary"}
            className="w-full"
          />
          <Button
            onClick={handleUpdatePassword}
            text={isSubmitting ? "Processing..." : "Update Password"}
            variant={isSubmitting ? "disabled" : "primary"}
            className="w-full"
          />
        </div>
        </form>
      </div>
    </ModalWrapper>
  );
};

export default PersonalPasswordModal;
